/**
 * Einheiten und Sätze — das Logbuch im Gym.
 *
 * Hier entsteht offline der größte Teil aller Daten: Zwischen Rack und Bank gibt
 * es kein Netz, und ein Satz, der erst nach dem Duschen gespeichert wird, wird
 * nicht gespeichert. Jede Funktion schreibt deshalb lokal und stellt die
 * Änderung in einem Zug in die Outbox.
 */

import {
  bestOneRepMax,
  bestPerDay,
  estimatedOneRepMaxOf,
  forgottenSessionEnd,
  isNewBest,
  newId,
  suggestFromLoggedSets,
  summarizeVolume,
  weeklyStreak,
  weekStart,
} from '@kraftwerk/shared';
import type {
  LoggedSet,
  ProgressionSuggestion,
  RepRange,
  TrackingMode,
  VolumeSummary,
} from '@kraftwerk/shared';
import { db } from './local.js';
import type {
  KraftwerkDatabase,
  LocalExercise,
  LocalWorkoutSession,
  LocalWorkoutSet,
} from './local.js';
import { stamp, writeRow, writeRows } from './write.js';

// --- Einheiten ---------------------------------------------------------------

export async function listSessions(
  database: KraftwerkDatabase = db,
  limit = 50,
): Promise<LocalWorkoutSession[]> {
  const rows = await database.workout_sessions.toArray();
  return rows
    .filter((row) => !row.deletedAt)
    .sort((a, b) => b.startedAt.localeCompare(a.startedAt))
    .slice(0, limit);
}

/**
 * Die laufende Einheit, falls es eine gibt.
 *
 * „Laufend" heißt: begonnen und nicht beendet. Ohne diesen Begriff würde ein
 * Wechsel in eine andere Ansicht — oder ein Neustart der App im Gym — die
 * Einheit verlieren.
 */
export async function openSession(
  database: KraftwerkDatabase = db,
): Promise<LocalWorkoutSession | null> {
  const sessions = await listSessions(database);
  return sessions.find((session) => !session.endedAt) ?? null;
}

export async function startSession(
  input: { title: string; planDayId?: string | null; bodyWeightKg?: number | null },
  database: KraftwerkDatabase = db,
): Promise<LocalWorkoutSession> {
  const now = stamp();
  const row: LocalWorkoutSession = {
    id: newId(),
    updatedAt: now,
    deletedAt: null,
    planDayId: input.planDayId ?? null,
    title: input.title.trim(),
    startedAt: now,
    endedAt: null,
    // Körpergewicht wird bei der Einheit festgehalten, nicht später aus dem
    // Profil nachgeschlagen — sonst wird jede alte Klimmzug-Schätzung falsch.
    bodyWeightKg: input.bodyWeightKg ?? null,
    note: null,
  };
  return writeRow('workout_sessions', row, 'upsert', database);
}

/**
 * Einheit beenden — mit „Wie lief's?" oder ohne.
 *
 * Ohne Bewertung bleibt `rating` leer, nicht 3: Eine geratene Mitte sähe aus wie
 * eine Antwort, und eine nicht bewertete Einheit ist weder gut noch schwach.
 */
/**
 * Zuletzt trainierte Übungen, neueste zuerst, jede einmal — für die
 * Schnellwahl beim Hinzufügen. Aus 40 Einträgen A–Z sucht niemand gern.
 */
export async function recentExerciseIds(
  limit = 6,
  database: KraftwerkDatabase = db,
): Promise<string[]> {
  const sets = (await database.workout_sets.toArray())
    .filter((set) => !set.deletedAt && set.completedAt)
    .sort((a, b) => b.completedAt!.localeCompare(a.completedAt!));
  const ids: string[] = [];
  for (const set of sets) {
    if (!ids.includes(set.exerciseId)) ids.push(set.exerciseId);
    if (ids.length === limit) break;
  }
  return ids;
}

/** Vergessene Einheit beenden — mit dem Ende aus `forgottenSessionEnd`. */
export async function endForgottenSession(
  id: string,
  database: KraftwerkDatabase = db,
): Promise<LocalWorkoutSession | null> {
  const existing = await database.workout_sessions.get(id);
  if (!existing || existing.deletedAt || existing.endedAt) return null;
  const sets = await database.workout_sets.where('sessionId').equals(id).toArray();
  const endedAt = forgottenSessionEnd(
    existing.startedAt,
    sets.filter((set) => !set.deletedAt).map((set) => set.completedAt),
  );
  return writeRow(
    'workout_sessions',
    { ...existing, endedAt, updatedAt: stamp() },
    'upsert',
    database,
  );
}

export async function endSession(
  id: string,
  database: KraftwerkDatabase = db,
  rating: number | null = null,
): Promise<LocalWorkoutSession | null> {
  const existing = await database.workout_sessions.get(id);
  if (!existing || existing.deletedAt) return null;
  const now = stamp();
  return writeRow(
    'workout_sessions',
    { ...existing, endedAt: now, rating: rating ?? existing.rating ?? null, updatedAt: now },
    'upsert',
    database,
  );
}

/** Bewertung nachtragen oder ändern — auch Tage später. */
export async function rateSession(
  id: string,
  rating: number | null,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const existing = await database.workout_sessions.get(id);
  if (!existing || existing.deletedAt) return;
  await writeRow(
    'workout_sessions',
    { ...existing, rating, updatedAt: stamp() },
    'upsert',
    database,
  );
}

export async function updateSession(
  id: string,
  changes: Partial<Pick<LocalWorkoutSession, 'title' | 'note' | 'bodyWeightKg'>>,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const existing = await database.workout_sessions.get(id);
  if (!existing || existing.deletedAt) return;
  await writeRow(
    'workout_sessions',
    { ...existing, ...changes, updatedAt: stamp() },
    'upsert',
    database,
  );
}

/**
 * Eine geplante Übung nur für diese Einheit tauschen (26.10.2026) — `null`
 * nimmt den Tausch zurück. Der Plan bleibt unberührt; schon geloggte Sätze
 * der geplanten Übung bleiben, wo sie sind.
 */
export async function swapForToday(
  sessionId: string,
  plannedExerciseId: string,
  replacementId: string | null,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const existing = await database.workout_sessions.get(sessionId);
  if (!existing || existing.deletedAt) return;
  const swaps = { ...(existing.swaps ?? {}) };
  if (replacementId === null || replacementId === plannedExerciseId) {
    delete swaps[plannedExerciseId];
  } else {
    swaps[plannedExerciseId] = replacementId;
  }
  await writeRow(
    'workout_sessions',
    { ...existing, swaps: Object.keys(swaps).length > 0 ? swaps : null, updatedAt: stamp() },
    'upsert',
    database,
  );
}

/** Einheit samt Sätzen weich löschen. */
export async function deleteSession(id: string, database: KraftwerkDatabase = db): Promise<void> {
  const session = await database.workout_sessions.get(id);
  if (!session || session.deletedAt) return;

  const now = stamp();
  const sets = (await database.workout_sets.where('sessionId').equals(id).toArray()).filter(
    (row) => !row.deletedAt,
  );

  await writeRows(
    [
      {
        table: 'workout_sessions',
        row: { ...session, deletedAt: now, updatedAt: now },
        kind: 'delete',
      },
      ...sets.map((set) => ({
        table: 'workout_sets' as const,
        row: { ...set, deletedAt: now, updatedAt: now },
        kind: 'delete' as const,
      })),
    ],
    database,
  );
}

// --- Sätze -------------------------------------------------------------------

export interface SetInput {
  weightKg?: number | null;
  reps?: number | null;
  seconds?: number | null;
  distanceM?: number | null;
  isWarmup?: boolean;
  side?: 'left' | 'right' | null;
  note?: string | null;
}

export async function listSets(
  sessionId: string,
  database: KraftwerkDatabase = db,
): Promise<LocalWorkoutSet[]> {
  const rows = await database.workout_sets.where('sessionId').equals(sessionId).toArray();
  return rows.filter((row) => !row.deletedAt).sort((a, b) => a.position - b.position);
}

export async function logSet(
  sessionId: string,
  exerciseId: string,
  input: SetInput,
  database: KraftwerkDatabase = db,
): Promise<LocalWorkoutSet | null> {
  const session = await database.workout_sessions.get(sessionId);
  if (!session || session.deletedAt) return null;

  const existing = await listSets(sessionId, database);
  const now = stamp();

  const row: LocalWorkoutSet = {
    id: newId(),
    updatedAt: now,
    deletedAt: null,
    sessionId,
    exerciseId,
    position: existing.length,
    weightKg: input.weightKg ?? null,
    reps: input.reps ?? null,
    seconds: input.seconds ?? null,
    distanceM: input.distanceM ?? null,
    bodyWeightKg: session.bodyWeightKg,
    isWarmup: input.isWarmup ?? false,
    side: input.side ?? null,
    completedAt: now,
    note: input.note ?? null,
  };

  return writeRow('workout_sets', row, 'upsert', database);
}

export async function updateSet(
  id: string,
  changes: SetInput,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const existing = await database.workout_sets.get(id);
  if (!existing || existing.deletedAt) return;
  await writeRow(
    'workout_sets',
    { ...existing, ...changes, updatedAt: stamp() },
    'upsert',
    database,
  );
}

export async function deleteSet(id: string, database: KraftwerkDatabase = db): Promise<void> {
  const existing = await database.workout_sets.get(id);
  if (!existing || existing.deletedAt) return;
  const now = stamp();
  await writeRow(
    'workout_sets',
    { ...existing, deletedAt: now, updatedAt: now },
    'delete',
    database,
  );
}

// --- Rückschau: letzte Werte, Vorschlag, Fortschritt -------------------------

async function setsOfExercise(
  exerciseId: string,
  database: KraftwerkDatabase,
): Promise<LocalWorkoutSet[]> {
  const rows = await database.workout_sets.where('exerciseId').equals(exerciseId).toArray();
  return rows.filter((row) => !row.deletedAt);
}

/**
 * Die Sätze der letzten Einheiten dieser Übung, neueste zuerst.
 *
 * Gruppiert wird über die Einheit, nicht über den Tag: Zwei Einheiten an einem
 * Tag sind zwei Datenpunkte, keiner.
 */
export async function recentSessionsOfExercise(
  exerciseId: string,
  database: KraftwerkDatabase = db,
  options: { excludeSessionId?: string | undefined; limit?: number } = {},
): Promise<{ sessionId: string; startedAt: string; sets: LocalWorkoutSet[] }[]> {
  const sets = await setsOfExercise(exerciseId, database);
  if (sets.length === 0) return [];

  const sessions = await database.workout_sessions.bulkGet([
    ...new Set(sets.map((set) => set.sessionId)),
  ]);
  const startedById = new Map(
    sessions
      .filter((session): session is LocalWorkoutSession => !!session && !session.deletedAt)
      .map((session) => [session.id, session.startedAt]),
  );

  const grouped = new Map<string, LocalWorkoutSet[]>();
  for (const set of sets) {
    if (set.sessionId === options.excludeSessionId) continue;
    if (!startedById.has(set.sessionId)) continue;
    const list = grouped.get(set.sessionId) ?? [];
    list.push(set);
    grouped.set(set.sessionId, list);
  }

  return [...grouped.entries()]
    .map(([sessionId, entries]) => ({
      sessionId,
      startedAt: startedById.get(sessionId) ?? '',
      sets: entries.sort((a, b) => a.position - b.position),
    }))
    .sort((a, b) => b.startedAt.localeCompare(a.startedAt))
    .slice(0, options.limit ?? 5);
}

/** Die Sätze vom letzten Mal — die Referenz, an der man sich im Gym orientiert. */
export async function lastSetsOfExercise(
  exerciseId: string,
  database: KraftwerkDatabase = db,
  excludeSessionId?: string,
): Promise<{ startedAt: string; sets: LocalWorkoutSet[] } | null> {
  const [latest] = await recentSessionsOfExercise(exerciseId, database, {
    excludeSessionId,
    limit: 1,
  });
  return latest ? { startedAt: latest.startedAt, sets: latest.sets } : null;
}

function toLoggedSets(sets: readonly LocalWorkoutSet[]): LoggedSet[] {
  return sets.map((set) => ({
    weightKg: set.weightKg,
    reps: set.reps,
    seconds: set.seconds,
    distanceM: set.distanceM,
    bodyWeightKg: set.bodyWeightKg,
    isWarmup: set.isWarmup,
  }));
}

/**
 * Gewichtsvorschlag für die nächste Einheit dieser Übung.
 *
 * Die Regel selbst steht in `@kraftwerk/shared` und wird dort getestet — sie
 * darf nicht zweimal existieren, sonst behauptet die App etwas anderes als der
 * Scheduler.
 */
export async function suggestionForExercise(
  exercise: LocalExercise,
  range: RepRange,
  database: KraftwerkDatabase = db,
  excludeSessionId?: string,
): Promise<ProgressionSuggestion | null> {
  const recent = await recentSessionsOfExercise(exercise.id, database, {
    excludeSessionId,
    limit: 2,
  });
  if (recent.length === 0) return null;

  // Älteste zuerst — `suggestFromLoggedSets` liest die letzte als jüngste.
  const sessions = [...recent].reverse().map((entry) => toLoggedSets(entry.sets));
  return suggestFromLoggedSets(sessions, exercise.trackingMode, range, exercise.incrementKg);
}

export interface ExerciseProgress {
  bestOneRepMaxKg: number | null;
  lastOneRepMaxKg: number | null;
  totalSets: number;
  /** Bestes geschätztes Maximum je Trainingstag (Ortszeit), älteste zuerst. */
  history: { day: string; kg: number }[];
}

export async function progressOfExercise(
  exercise: LocalExercise,
  database: KraftwerkDatabase = db,
  timeZone = 'Europe/Berlin',
): Promise<ExerciseProgress> {
  const sets = (await setsOfExercise(exercise.id, database)).filter((set) => !set.isWarmup);
  const estimates = sets
    .map((set) => ({
      value: estimatedOneRepMaxOf(toLoggedSets([set])[0] as LoggedSet, exercise.trackingMode),
      at: set.completedAt ?? '',
    }))
    .filter((entry): entry is { value: number; at: string } => entry.value !== null);

  const latest = [...estimates].sort((a, b) => b.at.localeCompare(a.at))[0] ?? null;

  return {
    bestOneRepMaxKg: estimates.length > 0 ? Math.max(...estimates.map((e) => e.value)) : null,
    lastOneRepMaxKg: latest?.value ?? null,
    totalSets: sets.length,
    history: bestPerDay(estimates, timeZone),
  };
}

/**
 * Für die Übungsliste: je Übung das beste geschätzte Maximum und wie oft sie
 * trainiert wurde — in **einem** Durchgang über alle Sätze, nicht je Zeile eine
 * Abfrage. Übungen ohne auswertbaren Satz fehlen in der Karte; die Liste sagt
 * dann ausdrücklich „noch nicht trainiert".
 */
export async function exerciseHighlights(
  database: KraftwerkDatabase = db,
): Promise<Map<string, { bestOneRepMaxKg: number | null; sessions: number }>> {
  const [sets, exercises] = await Promise.all([
    database.workout_sets.toArray(),
    database.exercises.toArray(),
  ]);
  const modeById = new Map<string, TrackingMode>(
    exercises.map((exercise) => [exercise.id, exercise.trackingMode]),
  );
  const result = new Map<string, { bestOneRepMaxKg: number | null; sessions: Set<string> }>();
  for (const set of sets) {
    if (set.deletedAt || set.isWarmup) continue;
    const mode = modeById.get(set.exerciseId);
    if (!mode) continue;
    const entry = result.get(set.exerciseId) ?? { bestOneRepMaxKg: null, sessions: new Set() };
    entry.sessions.add(set.sessionId);
    const estimate = estimatedOneRepMaxOf(toLoggedSets([set])[0] as LoggedSet, mode);
    if (estimate !== null && (entry.bestOneRepMaxKg === null || estimate > entry.bestOneRepMaxKg)) {
      entry.bestOneRepMaxKg = estimate;
    }
    result.set(set.exerciseId, entry);
  }
  return new Map(
    [...result].map(([id, entry]) => [
      id,
      { bestOneRepMaxKg: entry.bestOneRepMaxKg, sessions: entry.sessions.size },
    ]),
  );
}

export interface WeekVolume {
  /** Montag der Woche, `YYYY-MM-DD`. */
  week: string;
  summary: VolumeSummary;
  sessions: number;
}

/**
 * Wochenvolumen der letzten Wochen — inklusive der Sätze, die kein Volumen in
 * Kilogramm haben (Läufe, Planken). Die werden **ausgewiesen**, nicht
 * weggelassen: Eine Summe, die zwei Läufe unterschlägt, sieht aus wie eine
 * vollständige Antwort.
 */
export async function weeklyVolume(
  timeZone: string,
  database: KraftwerkDatabase = db,
  weeks = 6,
  now: Date = new Date(),
): Promise<WeekVolume[]> {
  const sessions = await listSessions(database, 500);
  const sets = (await database.workout_sets.toArray()).filter((row) => !row.deletedAt);
  const exercises = await database.exercises.toArray();

  const modeById = new Map<string, TrackingMode>(
    exercises.map((exercise) => [exercise.id, exercise.trackingMode]),
  );
  const sessionById = new Map(sessions.map((session) => [session.id, session]));

  const byWeek = new Map<
    string,
    { entries: { set: LoggedSet; mode: TrackingMode }[]; sessions: Set<string> }
  >();

  for (const set of sets) {
    const session = sessionById.get(set.sessionId);
    if (!session) continue;
    const mode = modeById.get(set.exerciseId);
    if (!mode) continue;

    const week = weekStart(new Date(session.startedAt), timeZone);
    const bucket = byWeek.get(week) ?? { entries: [], sessions: new Set<string>() };
    bucket.entries.push({ set: toLoggedSets([set])[0] as LoggedSet, mode });
    bucket.sessions.add(session.id);
    byWeek.set(week, bucket);
  }

  const currentWeek = weekStart(now, timeZone);
  const wanted: string[] = [];
  for (let index = 0; index < weeks; index += 1) {
    const [y, m, d] = currentWeek.split('-').map(Number) as [number, number, number];
    wanted.push(new Date(Date.UTC(y, m - 1, d - index * 7)).toISOString().slice(0, 10));
  }

  return wanted.map((week) => {
    const bucket = byWeek.get(week);
    return {
      week,
      summary: summarizeVolume(bucket?.entries ?? []),
      sessions: bucket?.sessions.size ?? 0,
    };
  });
}

/**
 * Wochen in Folge mit mindestens einer Einheit.
 *
 * Gezählt werden nur Einheiten, in denen **wirklich ein Satz** steht. Eine
 * begonnene und wieder verlassene Einheit ist kein Training; würde sie zählen,
 * ließe sich die Serie durch Antippen von „Starten" behaupten — und die
 * Anzeige stünde im Widerspruch zum Wochenvolumen, das ohnehin nur Sätze kennt.
 */
export async function trainingStreak(
  timeZone: string,
  database: KraftwerkDatabase = db,
  now: Date = new Date(),
): Promise<number> {
  const sessions = await listSessions(database, 500);
  const sets = (await database.workout_sets.toArray()).filter((row) => !row.deletedAt);
  const withSets = new Set(sets.map((set) => set.sessionId));

  return weeklyStreak(
    sessions
      .filter((session) => withSets.has(session.id))
      .map((session) => new Date(session.startedAt)),
    timeZone,
    now,
  );
}

/** Bestes geschätztes Maximum über eine Liste von Sätzen — für die Übungsansicht. */
export function bestEstimateOf(sets: readonly LocalWorkoutSet[]): number | null {
  return bestOneRepMax(
    sets
      .filter((set) => !set.isWarmup && set.weightKg !== null && set.reps !== null)
      .map((set) => ({ weightKg: set.weightKg ?? 0, reps: set.reps ?? 0 })),
  );
}

export interface NewBest {
  kg: number;
  previousKg: number;
}

/**
 * Satz eintragen und sagen, ob er ein neuer Bestwert ist (E13).
 *
 * Verglichen wird mit dem besten geschätzten Maximum **vor** diesem Satz —
 * dieselbe Rechnung wie im Fortschritt (`progressOfExercise`), damit die App
 * nie einen Rekord feiert, den die Fortschrittsseite nicht zeigt.
 */
export async function logSetWithBest(
  sessionId: string,
  exerciseId: string,
  input: SetInput,
  database: KraftwerkDatabase = db,
): Promise<{ set: LocalWorkoutSet | null; newBest: NewBest | null }> {
  const exercise = await database.exercises.get(exerciseId);
  const before = exercise ? await progressOfExercise(exercise, database) : null;
  const set = await logSet(sessionId, exerciseId, input, database);
  if (!set || !exercise) return { set, newBest: null };
  const candidate = estimatedOneRepMaxOf(
    toLoggedSets([set])[0] as LoggedSet,
    exercise.trackingMode,
  );
  const previous = before?.bestOneRepMaxKg ?? null;
  return {
    set,
    newBest:
      isNewBest(previous, candidate) && candidate !== null && previous !== null
        ? { kg: candidate, previousKg: previous }
        : null,
  };
}
